import { FunctionComponent, ReactNode, useMemo } from 'react'

import styled, { css, DefaultTheme } from 'styled-components'

import { desktopCSS } from '@measures/responsive'
import { Link, Logo } from '@utils/cook/types'
import useAppTheme from '@hooks/useAppTheme'
import useGetPageIdentity from '@hooks/useGetPageIdentity'
import useHasInvertedColors from '@hooks/useHasInvertedColors'
import Blink from '@components/Blink'

import { ArrowsVisibilityType } from './Swiper'

export type SectionHeaderProps = {
  title?: string
  link?: Link
  isSubheader?: boolean
  arrowsVisibility?: ArrowsVisibilityType
  accentColor: string
  className?: string
  children?: ReactNode
  logo?: Logo
  ctaText?: string
}

export interface SubheaderStyledProps {
  isSubheader?: boolean
}

export interface InvertedColorsStyledProps {
  hasInvertedColors: boolean
}

export interface AccentColorStyledProps {
  accentColor: string
}

export interface HasLinkStyledProps {
  hasLink?: boolean
}

interface StyledSectionHeaderProps {
  hasLogo?: boolean
  hasChildren?: boolean
}

const StyledSectionHeader = styled.div<
  SubheaderStyledProps & AccentColorStyledProps & StyledSectionHeaderProps
>`
  ${({
    theme: {
      spacing: { spacing2, spacing4, spacing8, spacing12, spacing16 },
    },
    accentColor,
    isSubheader,
    hasLogo,
    hasChildren,
  }) => css`
    box-sizing: border-box;
    width: 100%;
    ${!isSubheader &&
    css`
      border-style: solid;
      border-width: 0;
      border-color: ${accentColor};
      border-top-width: ${spacing2};
      border-left-width: ${spacing2};
      padding-top: ${spacing8};
      padding-left: ${spacing12};

      ${desktopCSS(css`
        border-top-width: ${spacing4};
        border-left-width: ${spacing4};
        padding-top: ${spacing12};
        padding-left: ${spacing16};
      `)};
    `};
    display: grid;
    grid-template-columns: auto ${hasLogo ? '1fr' : ''} minmax(0, auto);
    ${hasLogo &&
    css`
      grid-gap: ${spacing16};
    `}
    align-items: center;

    ${hasChildren &&
    css`
      > :last-child {
        justify-self: flex-end;
      }
    `}
    > :first-child {
      justify-self: flex-start;
    }
  `}
`

export type CommonTitleCSSArgs = {
  theme: DefaultTheme
} & InvertedColorsStyledProps &
  SubheaderStyledProps &
  AccentColorStyledProps &
  HasLinkStyledProps

const commonTitleCSS = ({
  theme: {
    spacing: { spacing8 },
    typography: {
      headings: {
        medium: { bundledCSS: mediumHeadingCSS },
        small: { bundledCSS: smallHeadingCSS },
      },
    },
    colors: { textDefault, staticWhite },
    utils: { ellipsis },
  },
  hasInvertedColors,
  isSubheader,
  accentColor,
  hasLink,
}: CommonTitleCSSArgs) => css`
  ${isSubheader
    ? css`
        ${smallHeadingCSS};
        min-height: 1.2em;
      `
    : mediumHeadingCSS};
  ${isSubheader &&
  !hasLink &&
  css`
    border-style: solid;
    border-width: 0;
    border-left-width: 2px;
    border-left-color: ${accentColor};
    padding-left: ${spacing8};
  `}
  color: ${hasInvertedColors ? staticWhite : textDefault};
  text-transform: uppercase;
  ${ellipsis('100%', 2)}
`

const StyledTitle = styled.h1<
  InvertedColorsStyledProps &
    SubheaderStyledProps &
    AccentColorStyledProps &
    HasLinkStyledProps
>`
  ${({ theme, hasInvertedColors, isSubheader, accentColor, hasLink }) => css`
    ${commonTitleCSS({
      theme,
      hasInvertedColors,
      isSubheader,
      accentColor,
      hasLink,
    })};

    margin: 0;
  `}
`

const StyledLinkTitle = styled(Blink)<
  InvertedColorsStyledProps & SubheaderStyledProps & AccentColorStyledProps
>`
  ${({ theme, hasInvertedColors, isSubheader, accentColor }) => css`
    display: block;
    appearance: none;
    text-decoration: none;
    ${commonTitleCSS({
      theme,
      hasInvertedColors,
      isSubheader,
      accentColor,
    })};

    &:hover {
      color: ${accentColor};
    }
  `}
`

const StyledChildrenWrapper = styled.div``

const StyledShowAllLink = styled(Blink)<
  InvertedColorsStyledProps & AccentColorStyledProps
>`
  ${({
    theme: {
      typography: {
        headings: {
          xxsmall: { bundledCSS: xxsmallHeadingCSS },
        },
      },
      colors: { staticWhite, textDefault },
      spacing: { spacing8, spacing16 },
    },
    hasInvertedColors,
    accentColor,
  }) => css`
    text-decoration: none;
    ${xxsmallHeadingCSS};
    color: ${hasInvertedColors ? staticWhite : textDefault};
    margin-left: ${spacing8};

    ${desktopCSS(css`
      margin-left: ${spacing16};
    `)}
    &:hover {
      color: ${accentColor};
    }
  `}
`

const StyledLogo = styled.img`
  height: fit-content;
  max-height: 32px;
  width: auto;
  max-width: 64px;
  object-fit: contain;
`

const StyledBlink = styled(Blink)`
  width: fit-content;
`
const SectionHeader: FunctionComponent<SectionHeaderProps> = (props) => {
  const {
    title,
    link,
    isSubheader,
    accentColor,
    className,
    logo,
    arrowsVisibility,
    ctaText,
  } = props
  const hasCustomComponent = !!props.children
  const hasInvertedColors = useHasInvertedColors()
  const hasLink = !!link
  const hasArrows =
    arrowsVisibility !== 'none' && arrowsVisibility !== undefined
  const showAllButtonEnabled =
    hasLink && !isSubheader && !hasArrows && !hasCustomComponent && !!ctaText
  const { isArticle } = useGetPageIdentity()
  const appTheme = useAppTheme()
  const resultedLogo =
    appTheme === 'dark' && logo?.imageUrlDark
      ? logo?.imageUrlDark
      : logo?.imageUrl

  const titleContent = useMemo(
    () => (
      <StyledTitle
        hasInvertedColors={hasInvertedColors}
        accentColor={accentColor}
        isSubheader={isSubheader}
        as={isArticle ? 'span' : 'h1'}
        hasLink={hasLink}
      >
        {title}
      </StyledTitle>
    ),
    [title, hasInvertedColors, accentColor, isSubheader, hasLink, isArticle],
  )

  if (!title) {
    return null
  }
  const hasLogo = !!logo
  const hasChildren = showAllButtonEnabled || hasCustomComponent

  return (
    <StyledSectionHeader
      isSubheader={isSubheader}
      accentColor={accentColor}
      className={className}
      hasChildren={hasChildren}
      hasLogo={hasLogo}
    >
      {hasLink ? (
        <StyledLinkTitle
          hasInvertedColors={hasInvertedColors}
          accentColor={accentColor}
          isSubheader={isSubheader}
          {...link}
        >
          {titleContent}
        </StyledLinkTitle>
      ) : (
        titleContent
      )}
      {hasLogo && (
        <StyledBlink {...logo?.link}>
          <StyledLogo src={resultedLogo} alt={logo?.name} loading="lazy" />
        </StyledBlink>
      )}
      {showAllButtonEnabled && (
        <StyledShowAllLink
          {...link}
          hasInvertedColors={hasInvertedColors}
          accentColor={accentColor}
        >
          {ctaText}
        </StyledShowAllLink>
      )}
      {hasCustomComponent && (
        <StyledChildrenWrapper>{props.children}</StyledChildrenWrapper>
      )}
    </StyledSectionHeader>
  )
}

export { StyledSectionHeader }

export default SectionHeader
